import { useMutation } from '@apollo/client';
import { emptyTranslation } from '@assets/i18n/config';
import { graphql } from '@generated/gql';
import {
  type AccountCurrencyTypes,
  TransactionObjectTypes,
  TransactionTypes,
} from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { NetWorthWorkflowContext } from '@ovComponents/4-module/netWorthWorkflow/netWorthWorkflowContext';
import { getNetWorthType } from '@ovComponents/4-module/widgets/netWorth/netWorthConstants';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { getCurrencyFlag, hasCurrencyFlag } from '@utils/currency';
import { dollars2cents } from '@utils/dollars2cents';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CREATE_TRANSACTION = graphql(`
  mutation createTransaction($input: CreateTransactionInput!) {
    createTransaction(input: $input) {
      transaction {
        id
      }
    }
  }
`);

const UPDATE_ACCOUNT = graphql(`
  mutation updateAccount($input: UpdateAccountInput!) {
    updateAccount(input: $input) {
      account {
        id
      }
    }
  }
`);

const NetWorthDetails = ({
  onNext,
  setStep,
}: {
  onNext: () => void;
  setStep: (step: number) => void;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'netWorth']);
  const { netWorthData, setNetWorthData } = useContext(NetWorthWorkflowContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [addMoreLoading, setAddMoreLoading] = useState<boolean>(false);
  const { baseCurrency, activeOrganization } = useContext(UserContext);
  const [userAvailableCurrencies, setUserAvailableCurrencies] = useState<
    (AccountCurrencyTypes | null | undefined)[]
  >([]);

  const applicableData =
    Object.keys(netWorthData).length !== 0 ? netWorthData : workflowData;
  const setApplicationContext =
    Object.keys(netWorthData).length !== 0 ? setNetWorthData : setWorkflowData;

  const handleValue = (value: Record<string, any>) =>
    setApplicationContext({
      ...applicableData,
      netWorthData: {
        ...applicableData.netWorthData,
        ...value,
      },
    });

  const [createTransaction] = useMutation(CREATE_TRANSACTION);
  const [updateAccount] = useMutation(UPDATE_ACCOUNT);

  useEffect(() => {
    const currencies = (
      (activeOrganization.productShelfCurrencies ?? []).length === 0
        ? [baseCurrency]
        : activeOrganization.productShelfCurrencies
    ) as AccountCurrencyTypes[];
    setUserAvailableCurrencies(currencies);
    if (currencies.length === 1) {
      handleValue({ netWorthCurrency: currencies[0] });
    }
  }, [baseCurrency, activeOrganization.productShelfCurrencies]);

  const createExternalAccount = async (afterCreation: () => void) => {
    setLoading(true);
    const { data: updateAccountData } = await updateAccount({
      variables: {
        input: {
          accountId: applicableData.currentAccountId,
          translatedNickName:
            applicableData.netWorthData.netWorthAccountNickname,
          baseCurrency:
            applicableData.netWorthData.netWorthCurrency ?? baseCurrency,
          availableCurrencies: [
            applicableData.netWorthData.netWorthCurrency ?? baseCurrency,
          ],
        },
      },
    });

    if (updateAccountData?.updateAccount?.account) {
      // wait for 2 seconds to ensure the account is updated in holding-service
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const { data: createTransactionData } = await createTransaction({
        variables: {
          input: {
            subAccountId: applicableData.currentSubAccountId,
            type: TransactionTypes.Other,
            date: dayjs().format('YYYY-MM-DD'),
            objectType: TransactionObjectTypes.Adjustment,
            valueCents:
              (applicableData.netWorthData.netWorthAmountCents ?? 0) *
              (getNetWorthType(
                applicableData.netWorthData?.externalSourceType,
              ) === 'liabilities'
                ? -1
                : 1),
          },
        },
      });

      if (createTransactionData?.createTransaction?.transaction) {
        afterCreation();
        setLoading(false);
      }
    }
  };

  const addAnotherExternalAccount = () => {
    setAddMoreLoading(true);
    setApplicationContext({
      ...applicableData,
      currentSubAccountId: undefined,
      currentAccountId: undefined,
      netWorthData: {
        ...applicableData.netWorthData,
        externalSourceType: undefined,
        netWorthAccountNickname: undefined,
        netWorthAmountCents: undefined,
        netWorthStep: 1,
        netWorthCurrency: undefined,
      },
    });
    setAddMoreLoading(false);
    setStep(1);
  };

  return (
    <WorkflowCompletionForm
      onSubmit={() => createExternalAccount(onNext)}
      submitButtonProps={{
        label: t('workflowCompletions:netWorthDetails.confirmButton'),
        disabled: loading,
        dataTestId: 'net-worth-details-continue-button',
      }}
      buttonFooter={
        <Box
          display='flex'
          justifyContent='flex-end'
          alignItems='flex-end'
          flex={1}
        >
          <Button
            sx={{ mr: 2, textAlign: 'center' }}
            dataTestId='net-worth-details-add-more-button'
            variant='tonal'
            onClick={() => createExternalAccount(addAnotherExternalAccount)}
            disabled={loading}
            label={t('workflowCompletions:netWorthDetails.addMoreButton')}
            loading={addMoreLoading}
          />
        </Box>
      }
    >
      <Box display='flex' flexDirection='column' gap={1.5} mb={3.5}>
        <Typography variant='displayLarge' sx={{ fontWeight: 400 }}>
          {t(
            `netWorthDetails.${applicableData.netWorthData.externalSourceType}`,
          )}
        </Typography>
      </Box>
      <Box display='flex' flexDirection='column' gap={2}>
        <TranslatableTextField
          testId='net-worth-account-nickname'
          label={t('netWorth:addNetWorthManually.nickname')}
          value={
            applicableData.netWorthData.netWorthAccountNickname ||
            emptyTranslation
          }
          onChange={(e: TranslatedString) =>
            handleValue({ netWorthAccountNickname: e })
          }
          fullWidth
        />
        <SelectField
          testId='net-worth-account-currency'
          label={t('netWorth:addNetWorthManually.currency')}
          value={applicableData.netWorthData.netWorthCurrency}
          onChange={(e: any) =>
            handleValue({ netWorthCurrency: e.target.value })
          }
          fullWidth
        >
          {userAvailableCurrencies?.map((currency: any) => (
            <MenuItem key={currency} value={currency}>
              <Box display='flex' alignItems='center' gap={1}>
                {currency && hasCurrencyFlag(currency) && (
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <img
                      src={getCurrencyFlag(currency)}
                      alt={currency}
                      style={{ width: '20px', height: '20px' }}
                    />
                  </Box>
                )}
                {t(`netWorth:addNetWorthManually.currencies.${currency}`)}
              </Box>
            </MenuItem>
          ))}
        </SelectField>
        <AmountField
          testId='net-worth-account-amount'
          label={t('netWorth:addNetWorthManually.value')}
          useFormatAmountValueCents
          amount={applicableData.netWorthData.netWorthAmountCents || 0}
          setAmount={(e: any) =>
            handleValue({
              netWorthAmountCents: dollars2cents(e, 2),
            })
          }
          fullWidth
        />
      </Box>
    </WorkflowCompletionForm>
  );
};

export default NetWorthDetails;
